import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { getUserDiagnostics, getClientLinkedDiagnostic, linkClientToDiagnostic, SavedDiagnostic } from '../../services/diagnosticService';
import { manualUnlockReport } from '../../services/paymentService';
import { getNotifications } from '../../services/notificationService';
import { supabase } from '../../lib/supabase';
import { dpsTheme } from '../../styles/dpsTheme';
import { DiagnosticCard } from './DiagnosticCard';
import { BarChart3 } from 'lucide-react';
import { cleanupPendingDiagnostic } from '../../utils/pendingDiagnosticStorage';
import { useDeviceSize } from '../../hooks/useMediaQuery';

async function getLatestSnapshot(diagnosticId: string) {
  const { data } = await supabase
    .from('score_snapshots')
    .select('score, score_protection, score_surveillance, score_dissuasion')
    .eq('diagnostic_id', diagnosticId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ?? null;
}

const STAFF_ROLES = ['advisor', 'company_admin', 'super_admin'];

export function ClientDashboard() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const isStaff = STAFF_ROLES.includes(userProfile?.role ?? '');
  const { isMobile } = useDeviceSize();
  const [diagnostics, setDiagnostics] = useState<SavedDiagnostic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifyingIds, setVerifyingIds] = useState<Set<string>>(new Set());
  const [unreadDiagnosticIds, setUnreadDiagnosticIds] = useState<Set<string>>(new Set());

  const uniqueDiagnostics = useMemo(() => {
    const seen = new Map<string, SavedDiagnostic>();
    diagnostics.forEach(d => { if (!seen.has(d.id)) seen.set(d.id, d); });
    return Array.from(seen.values());
  }, [diagnostics]);

  const loadDiagnostics = async (userId: string, role: string) => {
    let raw: SavedDiagnostic[];

    // Utiliser le rôle du profil, ou à défaut celui des métadonnées auth.
    // Cela couvre le cas où le profil public.users n'est pas encore chargé
    // (race condition) ou temporairement absent (post-activation).
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const effectiveRole = role || (authUser?.user_metadata?.role as string | undefined) || '';

    if (effectiveRole === 'client') {
      // Client invité : récupérer le diagnostic lié par client_id
      let linked = await getClientLinkedDiagnostic(userId);

      // Fallback : si pas encore lié, tenter de le lier via user_metadata
      // (couvre la race condition entre AuthContext et ce chargement)
      if (!linked) {
        const metaDiagnosticId = authUser?.user_metadata?.diagnostic_id as string | undefined;
        if (metaDiagnosticId) {
          await linkClientToDiagnostic(metaDiagnosticId, userId);
          linked = await getClientLinkedDiagnostic(userId);
        }
      }

      raw = linked ? [linked] : [];
    } else {
      // Staff / self-service : diagnostics créés par cet utilisateur
      raw = await getUserDiagnostics(userId);
    }

    const enriched = await Promise.all(
      (raw ?? []).map(async (diag) => {
        const snap = await getLatestSnapshot(diag.id);
        return snap
          ? { ...diag, current_score: snap.score, current_score_protection: snap.score_protection, current_score_surveillance: snap.score_surveillance, current_score_dissuasion: snap.score_dissuasion }
          : { ...diag, current_score: diag.security_score };
      })
    );
    return enriched;
  };

  useEffect(() => {
    let mounted = true;
    cleanupPendingDiagnostic();
    if (!currentUser) return;

    (async () => {
      try {
        setError(null);
        setLoading(true);
        const [enriched, notifs] = await Promise.all([
          loadDiagnostics(currentUser.id, userProfile?.role ?? ''),
          getNotifications(50).catch(() => []),
        ]);
        if (mounted) {
          setDiagnostics(enriched);
          const ids = new Set(
            notifs
              .filter(n => !n.read && n.data.diagnostic_id)
              .map(n => n.data.diagnostic_id!)
          );
          setUnreadDiagnosticIds(ids);
        }
      } catch {
        if (mounted) setError('Erreur lors du chargement des données.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [currentUser?.id, userProfile?.role]);

  const handleVerifyPayment = async (diagnosticId: string) => {
    setVerifyingIds(prev => new Set(prev).add(diagnosticId));
    try {
      const result = await manualUnlockReport(diagnosticId);
      if (result.success) {
        toast.success(result.alreadyUnlocked ? 'Ce rapport est déjà débloqué' : 'Rapport débloqué avec succès !');
        if (currentUser) setDiagnostics(await loadDiagnostics(currentUser.id, userProfile?.role ?? ''));
      } else {
        toast.error(result.error || 'Aucun paiement validé trouvé');
      }
    } catch {
      toast.error('Erreur lors de la vérification du paiement');
    } finally {
      setVerifyingIds(prev => { const s = new Set(prev); s.delete(diagnosticId); return s; });
    }
  };

  const paidReports = diagnostics.filter(d => d.report_paid);
  const latestScore = diagnostics.length > 0 ? ((diagnostics[0] as any).current_score ?? diagnostics[0].security_score) : null;
  const oldestScore = diagnostics.length > 1 ? ((diagnostics[diagnostics.length - 1] as any).current_score ?? diagnostics[diagnostics.length - 1].security_score) : null;
  const scoreEvolution = latestScore && oldestScore ? latestScore - oldestScore : null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: dpsTheme.colors.text.muted }}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '48px', textAlign: 'center' }}>
          <p style={{ color: dpsTheme.colors.text.muted }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: dpsTheme.typography.fontFamily, minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', color: dpsTheme.colors.text.secondary, overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? `${dpsTheme.spacing.lg} ${dpsTheme.spacing.md}` : dpsTheme.spacing['3xl'], width: '100%', boxSizing: 'border-box' }}>

        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <h1 style={{ fontSize: isMobile ? dpsTheme.typography.fontSize['2xl'] : dpsTheme.typography.fontSize['4xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary, marginBottom: '8px' }}>
            Tableau de bord
          </h1>
          <p style={{ fontSize: dpsTheme.typography.fontSize.lg, color: dpsTheme.colors.text.muted }}>
            Bienvenue{userProfile?.email ? `, ${userProfile.email}` : ''}
          </p>
        </div>

        {diagnostics.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: dpsTheme.spacing.lg, marginBottom: dpsTheme.spacing['2xl'] }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing.xl }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '12px', borderRadius: dpsTheme.borderRadius.md, background: dpsTheme.colors.accent.blue.bg, border: `1px solid ${dpsTheme.colors.accent.blue.border}` }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.accent.blue.primary} strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                </div>
                <div style={{ marginLeft: '16px' }}>
                  <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>Score actuel</p>
                  <p style={{ fontSize: dpsTheme.typography.fontSize['2xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>{latestScore}/100</p>
                </div>
              </div>
            </motion.div>

            {scoreEvolution !== null && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing.xl }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ padding: '12px', borderRadius: dpsTheme.borderRadius.md, background: scoreEvolution >= 0 ? dpsTheme.colors.scoreLevel.excellent.bg : dpsTheme.colors.scoreLevel.weak.bg, border: `1px solid ${scoreEvolution >= 0 ? dpsTheme.colors.scoreLevel.excellent.border : dpsTheme.colors.scoreLevel.weak.border}` }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={scoreEvolution >= 0 ? dpsTheme.colors.scoreLevel.excellent.primary : dpsTheme.colors.scoreLevel.weak.primary} strokeWidth="2">
                      <polyline points={scoreEvolution >= 0 ? '23 6 13.5 15.5 8.5 10.5 1 18' : '1 18 10.5 8.5 15.5 13.5 23 6'} />
                    </svg>
                  </div>
                  <div style={{ marginLeft: '16px' }}>
                    <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>Évolution</p>
                    <p style={{ fontSize: dpsTheme.typography.fontSize['2xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: scoreEvolution >= 0 ? dpsTheme.colors.scoreLevel.excellent.text : dpsTheme.colors.scoreLevel.weak.text }}>{scoreEvolution > 0 ? '+' : ''}{scoreEvolution}</p>
                  </div>
                </div>
              </motion.div>
            )}

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing.xl }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '12px', borderRadius: dpsTheme.borderRadius.md, background: dpsTheme.colors.status.completed.bg, border: `1px solid ${dpsTheme.colors.status.completed.border}` }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.status.completed.text} strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <div style={{ marginLeft: '16px' }}>
                  <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>Rapports premium</p>
                  <p style={{ fontSize: dpsTheme.typography.fontSize['2xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>{paidReports.length}</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        <div style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: isMobile ? dpsTheme.spacing.xl : dpsTheme.spacing['2xl'] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: dpsTheme.spacing.xl, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? dpsTheme.spacing.lg : 0 }}>
            <h2 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
              {isStaff ? `Mes diagnostics (${uniqueDiagnostics.length})` : 'Mon diagnostic'}
            </h2>
            {isStaff && (
              <button onClick={() => navigate('/diagnostic')} style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: 500, fontSize: '14px', cursor: 'pointer', border: 'none', background: dpsTheme.colors.accent.blue.gradient, color: 'white', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: dpsTheme.shadows.button, width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
                Nouveau diagnostic
              </button>
            )}
          </div>

          {uniqueDiagnostics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: isMobile ? '32px 16px' : '48px', background: dpsTheme.colors.background.darker, border: `1px solid ${dpsTheme.colors.border.secondary}`, borderRadius: dpsTheme.borderRadius.lg }}>
              <div style={{ marginBottom: '16px', opacity: 0.5 }}>
                <BarChart3 size={isMobile ? 48 : 64} strokeWidth={1.5} style={{ color: dpsTheme.colors.text.muted, margin: '0 auto' }} />
              </div>
              {isStaff ? (
                <>
                  <p style={{ color: dpsTheme.colors.text.muted, marginBottom: '24px' }}>Aucun diagnostic enregistré</p>
                  <button onClick={() => navigate('/diagnostic')} style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: 500, fontSize: '14px', cursor: 'pointer', border: 'none', background: dpsTheme.colors.accent.blue.gradient, color: 'white' }}>
                    Faire mon premier diagnostic
                  </button>
                </>
              ) : (
                <p style={{ color: dpsTheme.colors.text.muted }}>
                  Votre conseiller n'a pas encore partagé de diagnostic avec vous.
                </p>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.lg }}>
              {uniqueDiagnostics.map(diag => (
                <DiagnosticCard
                  key={diag.id}
                  diagnostic={diag}
                  onViewFreeReport={() => navigate('/results', { state: { formData: diag.diagnostic_data, diagnosticId: diag.id } })}
                  onViewPremiumReport={() => {
                    // Effacer le badge local au clic (la notification reste non-lue dans la cloche
                    // jusqu'à ce que l'advisor la marque explicitement comme lue)
                    setUnreadDiagnosticIds(prev => { const s = new Set(prev); s.delete(diag.id); return s; });
                    isStaff || diag.report_paid
                      ? navigate(`/dps/report/${diag.id}`)
                      : navigate('/checkout', { state: { formData: diag.diagnostic_data, diagnosticId: diag.id } });
                  }}
                  onVerifyPayment={() => handleVerifyPayment(diag.id)}
                  isVerifying={verifyingIds.has(diag.id)}
                  hidePaymentButtons={isStaff}
                  hasUnreadNotification={unreadDiagnosticIds.has(diag.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
